/**
 * Turning an ELK route into an SVG path.
 *
 * ELK gives us a polyline: a start point, zero or more bend points, and an end point, all axis
 * aligned because the router is configured for orthogonal routing.  Every interior point is
 * therefore a 90-degree corner, which is what lets the corner treatment be a simple quarter arc
 * rather than an approximation.
 *
 * Pure geometry, no React and no DOM, so it is tested directly.
 */
import type { Point } from "../layout";
import { drawnRadius, HOP_MIN_RADIUS, type Hop } from "../crossings";

/**
 * How the corners of the router's route are treated.
 *
 * Three real treatments, not four: `"curved"` used to be listed here but `buildPath` never
 * distinguished it from `"rounded"` -- the only difference was the radius the caller passed, so
 * `bezier` is `"rounded"` at 24px.  The union now matches what the code actually does.
 */
export type Corners = "rounded" | "sharp" | "none";

/** The corner radius a rounded route is drawn at where its runs allow it. */
export const CORNER_RADIUS = 24;

/**
 * Below this a route's own radius does not set the chart's: a route with one very short run
 * clamps itself alone rather than pull every corner on the chart down with it.
 */
export const OUTLIER_RADIUS = 12;

/**
 * The one radius a chart's corners are drawn at: the tightest its routes allow, ignoring outliers.
 */
export const chartRadius = (routes: Point[][], radius = CORNER_RADIUS): number => {
    const carried = routes.map(route => routeRadius(route, radius)).filter(r => r >= OUTLIER_RADIUS);
    return carried.length ? Math.min(...carried) : radius;
};

const length = (from: Point, to: Point) => Math.hypot(to.x - from.x, to.y - from.y);

const unit = (from: Point, to: Point) => {
    const distance = length(from, to);
    return distance === 0 ? { x: 0, y: 0 } : { x: (to.x - from.x) / distance, y: (to.y - from.y) / distance };
};

const round = (value: number) => Math.round(value * 100) / 100;

/** Midpoint of the longest segment: the roomiest place to put a label. */
export const labelAnchor = (points: Point[]): Point => {
    if (points.length < 2) {
        return points[0] ?? { x: 0, y: 0 };
    }

    let best = 0;
    let bestLength = -1;
    for (let i = 0; i < points.length - 1; i++) {
        const segment = length(points[i], points[i + 1]);
        if (segment > bestLength) {
            bestLength = segment;
            best = i;
        }
    }

    return {
        x: (points[best].x + points[best + 1].x) / 2,
        y: (points[best].y + points[best + 1].y) / 2,
    };
};

/**
 * Clearance either side of a break comes from the crossing pass, so the two cannot drift.
 *
 * Including the `HOP_MIN_RADIUS` floor, which lives there too: applying it only here meant the
 * pass merged against one radius and this drew another, and two crossings closer together than
 * their drawn breaks were left unmerged.  The floor is still applied a SECOND time below, where
 * a bridge squeezed between two corners is dropped rather than drawn too small to read.
 */
const hopRadius = (hop: Hop) => drawnRadius(hop.clearance);

/**
 * Which way a bridge bulges.
 *
 * Fixed by the axis rather than derived from the surrounding geometry, so it cannot flip between
 * relayouts: horizontal segments bow upwards, vertical ones to the left.  SVG's y axis points
 * down, so the sweep flag that achieves that depends on the direction of travel.
 */
const hopSweep = (direction: Point) => {
    if (Math.abs(direction.y) < 0.5) {
        return direction.x > 0 ? 1 : 0;
    }

    return direction.y > 0 ? 0 : 1;
};

/** How far a casing extends past the break it draws, so its ends are not flush. */
const CASING_OVERHANG = 2;

/** How much wider than the link a casing is drawn, so it clears the stroke it interrupts. */
export const CASING_WIDTH_MARGIN = 6;

/** Distance of a point along a segment, used to order bridges by travel direction. */
const along = (point: Hop, from: Point, direction: Point) =>
    (point.x - from.x) * direction.x + (point.y - from.y) * direction.y;

/** A break that survived the corner clamp, with the direction and radius it is drawn at. */
type PlacedHop = { hop: Hop; direction: Point; r: number };

/**
 * Where each break actually lands on the drawn path, keyed by segment and in travel order.
 *
 * The corner arcs own the ends of a segment, so a break near a bend is shrunk to fit and one
 * with nothing left is dropped.  Shared with `casingSegments` rather than done inside `buildPath`,
 * because a casing is only ever correct where this link's own stroke covers it: computed from the
 * raw polyline instead, a casing beside a bend was drawn up to a corner radius away from the path
 * -- a background-coloured gash through whatever ran underneath, and no stroke over it.
 */
/**
 * The one radius every corner of a route is drawn at: the largest all its runs can carry.  A run
 * ending on a node feeds one corner and gives it its whole length; a run between two corners
 * gives each half.
 */
export const routeRadius = (points: Point[], radius: number): number => {
    if (radius <= 0 || points.length < 3) {
        return Math.max(0, radius);
    }

    const budget = (i: number) => {
        const run = length(points[i], points[i + 1]);
        const terminal = i === 0 || i === points.length - 2;
        return terminal ? run : run / 2;
    };

    let r = radius;
    for (let j = 1; j < points.length - 1; j++) {
        const incoming = unit(points[j - 1], points[j]);
        const outgoing = unit(points[j], points[j + 1]);
        if (incoming.x * outgoing.y - incoming.y * outgoing.x === 0) continue;
        r = Math.min(r, budget(j - 1), budget(j));
    }
    return r;
};

const placeHops = (points: Point[], corners: Corners, borderRadius: number, hops: Hop[]) => {
    const placed = new Map<number, PlacedHop[]>();

    // `none` is a chord between the endpoints: it ignores the route, so there is nothing on it
    // for a break to sit on.
    if (points.length < 2 || corners === "none" || !hops.length) {
        return placed;
    }

    const radius = routeRadius(points, corners === "sharp" ? 0 : borderRadius);

    /**
     * The radius the corner at `points[j]` actually consumes, which is what a break on either
     * adjoining segment has to stay clear of.  Mirrors the clamp applied when the arc is emitted.
     */
    const cornerRadius = (j: number) => {
        if (j <= 0 || j >= points.length - 1) return 0;

        const incoming = unit(points[j - 1], points[j]);
        const outgoing = unit(points[j], points[j + 1]);
        if (incoming.x * outgoing.y - incoming.y * outgoing.x === 0) return 0;

        return radius;
    };

    for (let i = 0; i < points.length - 1; i++) {
        const from = points[i];
        const to = points[i + 1];
        const direction = unit(from, to);

        // The stretch of this segment a break may occupy: the corner arcs at either end own the
        // rest.  Without this a bridge near a bend emitted its exit PAST the corner's entry
        // point, so the path doubled back on itself before turning.
        const segment = length(from, to);
        const usableFrom = cornerRadius(i);
        const usableTo = segment - cornerRadius(i + 1);

        const onSegment = hops
            .filter(hop => hop.segment === i)
            .sort((a, b) => along(a, from, direction) - along(b, from, direction))
            .map((hop) => {
                // Shrink it to fit between the corners; what is dropped below is what cannot read
                // as a break at all, which is better than deforming the corner it would overrun.
                const at = along(hop, from, direction);
                return { hop, direction, r: Math.min(hopRadius(hop), at - usableFrom, usableTo - at) };
            })
            .filter(placement => placement.r >= HOP_MIN_RADIUS);

        if (onSegment.length) {
            placed.set(i, onSegment);
        }
    }

    return placed;
};

/**
 * Short strokes centred on each crossing, drawn in the page background colour underneath the
 * link's own stroke.  The link crossed *under* this one is interrupted; this one stays whole.
 *
 * `corners` and `borderRadius` are the SAME values handed to `buildPath` for this link, and they
 * are not optional in spirit: a casing the path does not cover is worse than no casing at all.
 */
export const casingSegments = (
    points: Point[],
    hops: Hop[],
    corners: Corners = "rounded",
    borderRadius = 0
): string[] =>
    [...placeHops(points, corners, borderRadius, hops).values()]
        .flat()
        .map(({ hop, direction, r }) => {
            // A shade wider than the break itself, so the casing's ends are not flush with the
            // stroke it is interrupting.
            const half = r + CASING_OVERHANG;
            const from = { x: hop.x - direction.x * half, y: hop.y - direction.y * half };
            const to = { x: hop.x + direction.x * half, y: hop.y + direction.y * half };
            return `M${round(from.x)},${round(from.y)} L${round(to.x)},${round(to.y)}`;
        });

/**
 * Build the `d` attribute for a route.
 *
 * Corners become exact quarter arcs, all at the one radius `routeRadius` allows the route, so a
 * tight run degrades every corner a little rather than making two of them sharp.
 *
 * `hops` inserts a semicircular bridge at each crossing this link is responsible for.
 */
export const buildPath = (points: Point[], corners: Corners, borderRadius = 0, hops: Hop[] = []): string => {
    if (!points.length) {
        return "";
    }

    if (points.length === 1) {
        return `M${round(points[0].x)},${round(points[0].y)}`;
    }

    // A straight chord between the endpoints, ignoring whatever the router decided.
    if (corners === "none") {
        const start = points[0];
        const end = points[points.length - 1];
        return `M${round(start.x)},${round(start.y)} L${round(end.x)},${round(end.y)}`;
    }

    const radius = routeRadius(points, corners === "sharp" ? 0 : borderRadius);
    let d = `M${round(points[0].x)},${round(points[0].y)}`;

    // Placed once, up front, so the casings drawn for this same link are placed identically.
    const placed = placeHops(points, corners, borderRadius, hops);

    for (let i = 0; i < points.length - 1; i++) {
        const from = points[i];
        const to = points[i + 1];
        const direction = unit(from, to);

        // Bridges on this segment, in the order they are met, already clamped to the stretch the
        // corner arcs leave free.
        for (const { hop, r } of placed.get(i) ?? []) {
            const entry = { x: hop.x - direction.x * r, y: hop.y - direction.y * r };
            const exit = { x: hop.x + direction.x * r, y: hop.y + direction.y * r };

            d += ` L${round(entry.x)},${round(entry.y)}`;
            d += ` A${round(r)},${round(r)} 0 0 ${hopSweep(direction)} ${round(exit.x)},${round(exit.y)}`;
        }

        // Round the corner at the far end, unless this is the last segment.
        const isCorner = i < points.length - 2;
        if (!isCorner) {
            d += ` L${round(to.x)},${round(to.y)}`;
            continue;
        }

        const next = points[i + 2];
        const outgoing = unit(to, next);
        const cross = direction.x * outgoing.y - direction.y * outgoing.x;
        const r = radius;

        // Collinear points are not corners -- ELK emits them, and rounding one would bow a
        // straight run. A zero radius means the caller asked for sharp corners.
        if (r <= 0 || cross === 0) {
            d += ` L${round(to.x)},${round(to.y)}`;
            continue;
        }

        const entry = { x: to.x - direction.x * r, y: to.y - direction.y * r };
        const exit = { x: to.x + outgoing.x * r, y: to.y + outgoing.y * r };
        // SVG's y axis points down, so a positive cross product is a clockwise turn on screen,
        // which is sweep-flag 1.
        const sweep = cross > 0 ? 1 : 0;

        d += ` L${round(entry.x)},${round(entry.y)}`;
        d += ` A${round(r)},${round(r)} 0 0 ${sweep} ${round(exit.x)},${round(exit.y)}`;
    }

    return d;
};
